import { appHooks, appTypes } from "@app/app";
import { useAppState } from "@app/app/hooks";
import { checkoutHooks } from "@app/checkout";
import { ControlledFormField, CurrencySelect, Label, LanguageSelect } from "@app/common";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogHeightModes,
    DialogTitle,
    DialogTrigger,
    DialogVerticalPositions,
} from "@app/componentLibrary/ui/dialog";
import { BugAntIcon } from "@heroicons/react/24/outline";
import { Button, tremorTwMerge } from "@ui";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

export const DebugDialog = () => {
    const { control, setValue } = useFormContext<appTypes.IBookingEngineState>();
    const { getState } = appHooks.useBookingEngineStateContext();
    const { property, language, currency } = getState();
    const { state: appState, globalState: globalAppState } = useAppState();
    const { t } = useTranslation("common");
    const {
        setState: setCheckoutSessionState,
        state: checkoutSessionState,
        isCheckoutSessionInitializing,
        isLoading: isCheckoutSessionLoading,
        reviveSession: reviveCheckoutSession,
        updateSessionExpiredAt: updateCheckoutSessionExpiredAt,
    } = checkoutHooks.useCheckoutSessionContext();

    const bookingEngineState = getState();

    return (
        <Dialog>
            <DialogTrigger>
                <Button data-testid="bug-ant-icon-btn" variant="ghost" size="icon" className="text-gray-500">
                    <BugAntIcon width={20} />
                </Button>
            </DialogTrigger>
            <DialogContent verticalPosition={DialogVerticalPositions.Middle} heightMode={DialogHeightModes.Full}>
                <DialogHeader>
                    <DialogTitle>Debug</DialogTitle>
                </DialogHeader>
                <div className="w-full p-4 pt-8 overflow-x-scroll cursor-auto">
                    <div className="mx-auto mt-10 text-left">
                        <h2 className="mb-2 text-2xl">Checkout session state</h2>
                        {renderNestedListByObject(checkoutSessionState)}
                    </div>

                    <div className="mx-auto mt-10 text-left">
                        <h2 className="mb-2 text-2xl">App state</h2>
                        {renderNestedListByObject(appState, 0, ["property"])}
                    </div>

                    <div className="mx-auto mt-10 text-left">
                        <h2 className="mb-2 text-2xl">Global App state</h2>
                        {renderNestedListByObject(globalAppState)}
                    </div>

                    <div className="mx-auto mt-10 text-left">
                        <h2 className="mb-2 text-2xl">Booking state</h2>
                        {renderNestedListByObject(bookingEngineState.bookingState, 0, ["property"])}
                    </div>

                    <div className="mx-auto mt-10 text-left">
                        <h2 className="mb-2 text-2xl">Booking engine state</h2>
                        {renderNestedListByObject(bookingEngineState, 0, [
                            "bookingState",
                            // "property.currencies",
                            // "property.rates",
                            // "property.rooms",
                        ])}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

const getLabelClassNameByLevel = (level: number) => {
    if (level === 0) return "text-xs font-bold";

    if (level === 1) return "text-xs font-semibold";

    if (level === 2) return "text-xs font-medium";

    return "text-xs font-normal";
};

const renderNestedListByObject = (data: any, level = 0, propertyBlacklist: string[] = [], parentKey = "") => (
    <ul className={tremorTwMerge("flex gap-1 flex-col text-left list-inside", `ml-${level > 0 ? level + 2 : 0}`)}>
        {Object.keys(data).map((key) => {
            const fullKey = parentKey !== "" ? `${parentKey}.${key}` : key;
            const shouldIgnore = propertyBlacklist.includes(fullKey);

            if (typeof data[key] === "object" && data[key] !== null) {
                return (
                    <li key={key} className="text-xs">
                        {<span className={getLabelClassNameByLevel(level)}>{key}</span>}:{" "}
                        {shouldIgnore ? "<ignored>" : renderNestedListByObject(data[key] as any, level + 1, propertyBlacklist, fullKey)}
                    </li>
                );
            } else {
                return (
                    <li key={key} className="text-xs">
                        {<span className={getLabelClassNameByLevel(level)}>{key}</span>}: {shouldIgnore ? "<ignored>" : data[key]}
                    </li>
                );
            }
        })}
    </ul>
);
